<!-- src/components/BugReports/QuickBugModal.vue -->
<template>
  <div
    class="fixed inset-0 z-modal-backdrop"
    data-bug-capture-hide="true"
    role="dialog"
    aria-modal="true"
  >
    <div class="flex min-h-screen items-end justify-center px-4 py-12 sm:items-center sm:p-0">
      <div class="fixed inset-0 bg-gray-500/75" aria-hidden="true" @click="close" />

      <div class="relative z-modal w-full max-w-md rounded-lg bg-white shadow-xl">
        <form @submit.prevent="handleSubmit">
          <div class="border-b border-gray-200 px-5 py-3">
            <h2 class="flex items-center gap-2 text-base font-semibold text-gray-900">
              <Bug class="icon-sm text-brand-blue" />
              Quick Bug Report
            </h2>
            <p class="mt-1 text-xs text-gray-500">
              We'll capture a screenshot, your recent activity, and console logs automatically. Have
              a feature idea instead? Use
              <a
                href="/bug-reports"
                class="text-brand-blue hover:underline"
                @click.prevent="goToFullForm"
                >the full form</a
              >.
            </p>
          </div>

          <div class="space-y-3 px-5 py-4">
            <div>
              <label class="block text-xs font-medium text-gray-700"
                >Short title <span class="text-red-500">*</span></label
              >
              <input
                v-model="title"
                type="text"
                maxlength="200"
                required
                class="form-input-sm mt-1 w-full"
                placeholder="e.g. Create page won't save"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700">Severity</label>
              <div
                class="mt-1 inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs"
              >
                <button
                  v-for="opt in SEVERITY_OPTIONS"
                  :key="opt.id"
                  type="button"
                  class="rounded-md px-2.5 py-1 font-medium transition"
                  :class="
                    severity === opt.id
                      ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                  "
                  @click="severity = opt.id"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700"
                >What went wrong? <span class="text-red-500">*</span></label
              >
              <textarea
                v-model="description"
                rows="4"
                required
                class="form-textarea mt-1 w-full text-sm"
                placeholder="Briefly describe what you saw and what you expected."
              />
            </div>

            <TicketWatcherPicker
              v-model="watcherUids"
              :submitter-uid="userStore.user?.uid"
              :disabled="isSubmitting"
            />

            <div
              v-if="progress"
              class="flex items-center gap-2 rounded-md bg-tint-blue p-2 text-xs text-blue-800"
            >
              <Loader2 class="icon-xs animate-spin" />
              {{ progress }}
            </div>
          </div>

          <div class="flex flex-row-reverse gap-2 border-t border-gray-200 bg-gray-50 px-5 py-2.5">
            <button
              type="submit"
              class="btn-primary-xs"
              :disabled="isSubmitting || !title.trim() || !description.trim()"
            >
              <Loader2 v-if="isSubmitting" class="icon-xs mr-1 animate-spin" />
              Capture &amp; Submit
            </button>
            <button type="button" class="btn-secondary-xs" :disabled="isSubmitting" @click="close">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Bug, Loader2 } from 'lucide-vue-next'
import TicketWatcherPicker from './TicketWatcherPicker.vue'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/composables/useToast'
import { captureAndSubmitQuickBug } from '@/composables/useBugCapture'
import { SEVERITY_OPTIONS, DEFAULT_SEVERITY } from './statusUtils'

const emit = defineEmits(['close', 'submitted'])
const toast = useToast()
const router = useRouter()

const userStore = useUserStore()
const watcherUids = ref([])
const title = ref('')
const description = ref('')
const severity = ref(DEFAULT_SEVERITY)
const isSubmitting = ref(false)
const progress = ref('')

function close() {
  if (isSubmitting.value) return
  emit('close')
}

function goToFullForm() {
  if (isSubmitting.value) return
  emit('close')
  router.push({ name: 'BugReports' }).catch(() => {})
}

async function handleSubmit() {
  if (isSubmitting.value) return
  isSubmitting.value = true
  progress.value = 'Capturing screenshot and recent activity…'
  try {
    const res = await captureAndSubmitQuickBug({
      watcherUids: watcherUids.value,
      title: title.value,
      description: description.value,
      severity: severity.value,
    })
    toast.success('Bug report submitted — thank you!')
    emit('submitted', res?.bugReportId || null)
  } catch (err) {
    console.error('[QuickBugModal] Submit failed:', err)
    toast.error(err?.message || 'Failed to submit bug report.')
  } finally {
    isSubmitting.value = false
    progress.value = ''
  }
}
</script>
